import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table from "@via/components/table/table.tsx";
import SshKeysModal from "@via/pages/account/profile/credentials/table/ssh-keys-modal.tsx";
import { useDeleteCredential } from "@via/pages/account/profile/profile.api.tsx";
import {
  CREDENTIAL_KEYS_TABLE,
  CREDENTIAL_TYPES,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import type { TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import {
  AmazonCredentials,
  GetUserCredentialsResponse,
  GoogleCredentials,
  Ssh,
} from "common";
import { JSX, useState } from "react";
import S3Logo from "/aws-s3-logo.svg";
import GCSLogo from "/gcs-logo.svg";
import SSHLogo from "/ssh-logo.png";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";

type Credential =
  | (AmazonCredentials & { type: CREDENTIAL_TYPES.AMAZON })
  | (GoogleCredentials & { type: CREDENTIAL_TYPES.GOOGLE })
  | (Ssh & { type: CREDENTIAL_TYPES.SSH });

const columnHelper = createColumnHelper<Credential>();

const getCredentialIcon = (credentialType: CREDENTIAL_TYPES) => {
  if (credentialType === CREDENTIAL_TYPES.AMAZON) {
    return <img className="mr-3 h-6 w-6" alt={"aws-Logo"} src={S3Logo} />;
  } else if (credentialType === CREDENTIAL_TYPES.GOOGLE) {
    return <img className="mr-3 h-6 w-6" alt={"gcs-Logo"} src={GCSLogo} />;
  } else if (credentialType === CREDENTIAL_TYPES.SSH) {
    return <img className="mr-3 h-6 w-6" alt={"gcs-Logo"} src={SSHLogo} />;
  } else {
    return "";
  }
};

export default function CredentialKeysTable({
  credentials,
  onEditSshCredential,
}: {
  credentials: GetUserCredentialsResponse | Record<string, never>;
  onEditSshCredential?: (credential: Ssh) => void;
}): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const [selectedSshPublicKey, setSelectedSshPublicKey] = useState<
    string | null
  >(null);

  const {
    amazonCredentials = [],
    googleCredentials = [],
    ssh = [],
  } = credentials as GetUserCredentialsResponse;

  const [credentialToBeDeleted, setCredentialToBeDeleted] =
    useState<Credential | null>(null);

  const amazonCredentialsFormatted: Credential[] = amazonCredentials.map(
    credential => ({
      ...credential,
      type: CREDENTIAL_TYPES.AMAZON,
    })
  );

  const googleCredentialsFormatted: Credential[] = googleCredentials.map(
    credential => ({
      ...credential,
      type: CREDENTIAL_TYPES.GOOGLE,
    })
  );

  const sshCredentialsFormatted: Credential[] = ssh.map(credential => ({
    ...credential,
    type: CREDENTIAL_TYPES.SSH,
  }));

  const combinedCredentials = [
    ...amazonCredentialsFormatted,
    ...googleCredentialsFormatted,
    ...sshCredentialsFormatted,
  ];

  const {
    mutateAsync: deleteCredential,
    isPending: isDeleteCredentialLoading,
  } = useDeleteCredential();

  const handleRemoveAction = async (credential: Credential | null) => {
    switch (credential?.type) {
      case CREDENTIAL_TYPES.AMAZON:
        await deleteCredential({ amazonCredentialId: credential.id });
        break;
      case CREDENTIAL_TYPES.GOOGLE:
        await deleteCredential({ googleCredentialId: credential.id });
        break;
      case CREDENTIAL_TYPES.SSH:
        await deleteCredential({ sshId: credential.id });
        break;
      default:
        break;
    }
  };

  const handleViewSshKeysModal = (publicKey: string | null) => {
    setSelectedSshPublicKey(publicKey);
  };

  const handleAction = (
    option: TableContextMenuInterface,
    credential: Credential
  ) => {
    switch (option.name) {
      case "View":
        if (credential.type === CREDENTIAL_TYPES.SSH && credential?.publicKey) {
          handleViewSshKeysModal(credential.publicKey);
        }
        break;
      case "Edit":
        if (
          credential.type === CREDENTIAL_TYPES.SSH &&
          isAdmin &&
          onEditSshCredential
        ) {
          onEditSshCredential(credential);
        }
        break;
      case "Remove":
        setCredentialToBeDeleted(credential);
        break;
      default:
        break;
    }
  };

  const columns = [
    columnHelper.accessor("type", {
      header: () => <span>Type</span>,
      cell: info => getCredentialIcon(info.getValue()),
    }),
    columnHelper.accessor("name", {
      header: () => <span>Credential Name</span>,
      cell: info => info.getValue(),
    }),
    columnHelper.accessor("dateCreated", {
      header: () => <span>Date Created</span>,
      cell: info => {
        const value = info.getValue();
        return <span>{value ? formatDateString(value, "LLL") : ""}</span>;
      },
    }),
    columnHelper.accessor("id", {
      header: () => "Options",
      cell: ({ row }) => {
        if (
          !isAdmin &&
          (row.original!.type === CREDENTIAL_TYPES.SSH ||
            row.original!.type === CREDENTIAL_TYPES.AMAZON) &&
          row.original!.hide
        ) {
          return null;
        }

        return (
          <TableContextMenu
            options={CREDENTIAL_KEYS_TABLE.filter(({ name }) => {
              if (name === "View") {
                return (
                  row.original!.type === CREDENTIAL_TYPES.SSH &&
                  row.original?.publicKey
                );
              }
              if (name === "Edit") {
                return isAdmin && row.original!.type === CREDENTIAL_TYPES.SSH;
              }
              return true;
            })}
            onSelect={option => {
              handleAction(option, row.original);
            }}
          />
        );
      },
      enableSorting: false,
    }),
  ] as Array<ColumnDef<Credential>>;

  return (
    <div>
      <Table
        columns={columns}
        data={combinedCredentials || []}
        total={combinedCredentials?.length}
      />
      {selectedSshPublicKey && (
        <SshKeysModal
          publicKey={selectedSshPublicKey}
          setPublicKey={handleViewSshKeysModal}
        />
      )}
      <DeleteModal
        item={credentialToBeDeleted}
        setItem={setCredentialToBeDeleted}
        deleteFunction={() => handleRemoveAction(credentialToBeDeleted)}
        isLoading={isDeleteCredentialLoading}
        title="Delete Credential"
      />
    </div>
  );
}
